import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import {
  AddWalletPaymentMethod,
  isReusablePaymentMethod,
  isReusablePaymentMethodIdentifier,
  MARKETPLACE_ERROR_HANDLERS,
  PaymentMethodCreated,
  PaymentMethodDeleted,
  PaymentMethodInfo,
  PaymentMethodType,
  PaymentMethodUpdated,
  WalletPaymentMethodInfo,
  WalletPaymentMethodType
} from '@platform-frontend/marketplace';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { handleErrors, InvalidStateError, RefreshWindow, ShowErrorNotification, throwIfNullish } from '@platform-frontend/util';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { FetchFlowData, FlowCanceled, FlowStarted, PublishFetchedFlowData, SetFlow } from '../../flow';
import { MSG_SELECT_PAYMENT_METHOD_DEFAULT } from '../../notification-messages';
import { CheckoutPaymentInformationState } from '../../payment-information';
import { CheckWalletAutoSelection, PaymentWalletState } from '../../payment-wallet';
import { ShoppingCartPaymentStateModel } from './model/shopping-cart-payment-state-model';
import { SelectPaymentMethod } from './shopping-cart-payment.actions';

const DEFAULT_STATE: ShoppingCartPaymentStateModel = {
  selectedPaymentMethod: null,
  loadedSelectedPaymentMethod: null
};

@State<ShoppingCartPaymentStateModel>({
  name: 'shoppingCartPayment',
  defaults: DEFAULT_STATE
})
@Injectable()
export class ShoppingCartPaymentState {
  constructor(private http: HttpClient, private store: Store) {}

  paymentMethods$: Observable<PaymentMethodInfo[]> = this.store.select(CheckoutPaymentInformationState.paymentMethods);
  walletPaymentMethods$: Observable<WalletPaymentMethodInfo[]> = this.store.select(CheckoutPaymentInformationState.walletPaymentMethods);
  addablePaymentMethodTypes$: Observable<PaymentMethodType[]> = this.store.select(CheckoutPaymentInformationState.addablePaymentMethodTypes);
  autoselectableWallet$: Observable<WalletPaymentMethodType | null> = this.store.select(PaymentWalletState.autoselectableWallet);

  @Selector()
  static selectedPaymentMethod(state: ShoppingCartPaymentStateModel): PaymentMethodInfo | null {
    return state.selectedPaymentMethod;
  }

  @Action(FetchFlowData)
  loadSelectedPaymentMethod(ctx: StateContext<ShoppingCartPaymentStateModel>, { flow }: FetchFlowData): Observable<unknown> {
    if (flow.type !== 'COMMERCIAL_CHECKOUT') {
      return of(null);
    }

    return this.http
      .get<PaymentMethodInfo | null>(PLATFORM_ENVIRONMENT.apiUrl + '/paymentInfo/active')
      .pipe(tap(loadedSelectedPaymentMethod => ctx.patchState({ loadedSelectedPaymentMethod })));
  }

  @Action(PublishFetchedFlowData)
  publishSelectedPaymentMethod(ctx: StateContext<ShoppingCartPaymentStateModel>): void {
    const { loadedSelectedPaymentMethod } = ctx.getState();
    ctx.patchState({
      selectedPaymentMethod: loadedSelectedPaymentMethod,
      loadedSelectedPaymentMethod: DEFAULT_STATE.loadedSelectedPaymentMethod
    });
  }

  @Action(SetFlow)
  @Action(RefreshWindow)
  @Action(FlowCanceled)
  reset(ctx: StateContext<ShoppingCartPaymentStateModel>): void {
    ctx.patchState(DEFAULT_STATE);
  }

  @Action(FlowStarted)
  autoSelectWallet(ctx: StateContext<ShoppingCartPaymentStateModel>, { flow }: FlowStarted): Observable<unknown> {
    return flow.type === 'COMMERCIAL_CHECKOUT' && ctx.getState().selectedPaymentMethod === null
      ? ctx.dispatch(new CheckWalletAutoSelection()).pipe(
          switchMap(() => this.autoselectableWallet$),
          take(1),
          switchMap(autoselectableWallet => (autoselectableWallet ? this.autoSelectCheckedWallet(ctx, autoselectableWallet) : of(null)))
        )
      : of(null);
  }

  private autoSelectCheckedWallet(ctx: StateContext<ShoppingCartPaymentStateModel>, wallet: WalletPaymentMethodType): Observable<unknown> {
    return combineLatest([this.walletPaymentMethods$, this.addablePaymentMethodTypes$]).pipe(
      take(1),
      switchMap(([walletPaymentMethods, addablePaymentMethodTypes]) =>
        walletPaymentMethods.find(method => method.type === wallet)
          ? ctx.dispatch(new SelectPaymentMethod({ type: wallet }))
          : addablePaymentMethodTypes.includes(wallet)
          ? ctx.dispatch(new AddWalletPaymentMethod(wallet))
          : of(null)
      )
    );
  }

  @Action(SelectPaymentMethod)
  selectPaymentMethod(ctx: StateContext<ShoppingCartPaymentStateModel>, { paymentMethodIdentifier }: SelectPaymentMethod): Observable<unknown> {
    return this.paymentMethods$.pipe(
      take(1),
      map(paymentMethods =>
        paymentMethods.find(paymentMethod =>
          isReusablePaymentMethodIdentifier(paymentMethodIdentifier)
            ? isReusablePaymentMethod(paymentMethod) && paymentMethod.id === paymentMethodIdentifier.id
            : paymentMethod.type === paymentMethodIdentifier.type
        )
      ),
      throwIfNullish(() => new InvalidStateError(`${paymentMethodIdentifier} does not correspond to a known payment method.`)),
      tap(selectedPaymentMethod => ctx.patchState({ selectedPaymentMethod })),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        DEFAULT: new ShowErrorNotification(MSG_SELECT_PAYMENT_METHOD_DEFAULT)
      })
    );
  }

  @Action(PaymentMethodCreated)
  autoSelectCreatedPaymentMethod(ctx: StateContext<ShoppingCartPaymentStateModel>, { paymentMethod }: PaymentMethodCreated): void {
    ctx.patchState({ selectedPaymentMethod: paymentMethod });
  }

  @Action(PaymentMethodUpdated)
  updateSelectedPaymentMethod(ctx: StateContext<ShoppingCartPaymentStateModel>, { original, update }: PaymentMethodUpdated): void {
    const selectedPaymentMethod = ctx.getState().selectedPaymentMethod;
    if (selectedPaymentMethod && isReusablePaymentMethod(selectedPaymentMethod) && original.id === selectedPaymentMethod.id) {
      ctx.patchState({ selectedPaymentMethod: update });
    }
  }

  @Action(PaymentMethodDeleted)
  deleteSelectedPaymentMethod(ctx: StateContext<ShoppingCartPaymentStateModel>, { paymentMethod }: PaymentMethodDeleted): Observable<unknown> {
    const selectedPaymentMethod = ctx.getState().selectedPaymentMethod;
    if (
      (isReusablePaymentMethod(paymentMethod) &&
        selectedPaymentMethod &&
        isReusablePaymentMethod(selectedPaymentMethod) &&
        paymentMethod.id === selectedPaymentMethod.id) ||
      (!isReusablePaymentMethod(paymentMethod) && selectedPaymentMethod?.type === paymentMethod.type)
    ) {
      return this.paymentMethods$.pipe(
        take(1),
        map(paymentMethods => paymentMethods[0] ?? null),
        tap(newPaymentMethod => ctx.patchState({ selectedPaymentMethod: newPaymentMethod }))
      );
    } else {
      return of(null);
    }
  }
}
